import { useState } from 'react';
import { Checkbox, Header, Message } from 'semantic-ui-react';
import Reconciliations from '../../../reconciliations/reconciliations';
import type { ReconciliationEvent } from '../../../../../types/reconciliation.types';
import BranchComparisonDetails from './branchComparisonDetails';
import { MODEL_DIFF_DISPLAY_MODE } from './branchComparisonDetails/constants';
import type { BranchComparison, BranchData, InvalidFieldType } from './branchComparisonDetails/types';
import CollapsibleSection from './components/collapsibleSection';
import InvalidFieldsList from './components/invalidFieldsList';
import BranchDiffLegend from './components/legend';
import './styles.css';

type ReconciliationData = {
    events: ReconciliationEvent[];
    descriptorsMap?: Record<string, any>;
    schemasKnowledge?: Record<number, { branchType: string; reviewType: string }>;
};

type BranchDiffProps = {
    invalidFields: InvalidFieldType[];
    editToReviewComparison?: BranchComparison;
    mainToEditComparison?: BranchComparison;
    coreDataDiffOnly?: boolean;
    companyId?: string;
    reconciliation?: ReconciliationData;
};

type SectionKey = 'editToReview' | 'mainToEdit' | 'reconciliations';

function formatComparisonTitle(referenceBranch: BranchData, targetBranch: BranchData) {
    const formatBranchLabel = (branch: BranchData) => {
        const name = branch.type[0] + branch.type.slice(1).toLowerCase();
        return branch.schema.id == null ? name : `${name} (${branch.schema.id})`;
    };
    return `${formatBranchLabel(targetBranch)} → ${formatBranchLabel(referenceBranch)}`;
}

function BranchDiff({
    invalidFields,
    editToReviewComparison,
    mainToEditComparison,
    coreDataDiffOnly,
    companyId,
    reconciliation,
}: BranchDiffProps) {
    const [summarized, setSummarized] = useState(false);

    const [activeSections, setActiveSections] = useState<Set<SectionKey>>(
        () => new Set(['editToReview', 'mainToEdit', 'reconciliations']),
    );
    const toggleSection = (key: SectionKey) => {
        setActiveSections((previous) => {
            const next = new Set(previous);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    return (
        <div>
            <Header as="h4">
                Branch Differences
                <Checkbox
                    toggle
                    className="floatRight"
                    label="Summarized view"
                    checked={summarized}
                    onChange={(_, { checked }) => setSummarized(!!checked)}
                />
            </Header>
            {editToReviewComparison && (
                <CollapsibleSection
                    title={formatComparisonTitle(
                        editToReviewComparison.referenceBranch,
                        editToReviewComparison.targetBranch,
                    )}
                    sectionKey="editToReview"
                    activeSections={activeSections}
                    onToggle={toggleSection}
                    className="branch-diff__accordion--edit-to-review"
                >
                    <BranchComparisonDetails
                        comparison={editToReviewComparison}
                        summarized={summarized}
                        coreDataDiffOnly={coreDataDiffOnly}
                        companyId={companyId}
                    />
                </CollapsibleSection>
            )}
            <InvalidFieldsList fields={invalidFields} />
            {mainToEditComparison && (
                <CollapsibleSection
                    title={formatComparisonTitle(
                        mainToEditComparison.referenceBranch,
                        mainToEditComparison.targetBranch,
                    )}
                    sectionKey="mainToEdit"
                    activeSections={activeSections}
                    onToggle={toggleSection}
                    className="branch-diff__accordion--main-to-edit"
                >
                    {mainToEditComparison.modelDisplayMode === MODEL_DIFF_DISPLAY_MODE.DETAILED ? (
                        <BranchComparisonDetails
                            comparison={mainToEditComparison}
                            summarized={summarized}
                            coreDataDiffOnly={coreDataDiffOnly}
                            companyId={companyId}
                        />
                    ) : (
                        <Message error>Not available at this stage</Message>
                    )}
                </CollapsibleSection>
            )}
            {(editToReviewComparison || mainToEditComparison) && <BranchDiffLegend />}
            {!!reconciliation?.events.length && (
                <CollapsibleSection
                    title="Reconciliations"
                    sectionKey="reconciliations"
                    activeSections={activeSections}
                    onToggle={toggleSection}
                >
                    <Reconciliations
                        reconciliationEvents={reconciliation.events}
                        descriptorsMap={reconciliation.descriptorsMap ?? {}}
                        schemasKnowledge={reconciliation.schemasKnowledge ?? {}}
                    />
                </CollapsibleSection>
            )}
        </div>
    );
}

export default BranchDiff;
